import dotenv from 'dotenv';
import { z } from 'zod';
dotenv.config();

/**
 * Server-side environment variables schema
 * Add all required server env vars here.
 */
const serverSchema = z.object({
    NODE_ENV: z.enum(['development', 'test', 'production']),
    DATABASE_URL: z.string().url(),
    SMTP_HOST: z.string().min(1),
    SMTP_PORT: z.coerce.number().positive(),
    SMTP_USER: z.string().min(1),
    SMTP_PASS: z.string().min(1),
    SMTP_FROM: z.string().min(1),
    NEXT_PUBLIC_APP_URL: z.string().url(),
    GOOGLE_CLIENT_ID: z.string().min(1),
    GOOGLE_CLIENT_SECRET: z.string().min(1),
    MICROSOFT_TENANT_ID: z.string().min(1),
    MICROSOFT_CLIENT_ID: z.string().min(1),
    MICROSOFT_CLIENT_SECRET: z.string().min(1),
    NEXT_PUBLIC_PLAUSIBLE_SITE: z.string().min(1),
    NEXT_PUBLIC_PLAUSIBLE_URL: z.string().min(1),
    R2_BUCKET: z.string().min(1),
    R2_ACCESS_KEY_ID: z.string().min(1),
    R2_SECRET_ACCESS_KEY: z.string().min(1),
    R2_ACCOUNT_ID: z.string().min(1),
    R2_DOMAIN: z.string().min(1),

    // Example:
    // NEXTAUTH_SECRET: z.string().min(1),
});

/**
 * Client-side environment variables schema
 * Add all required public env vars here (must start with NEXT_PUBLIC_)
 */
const clientSchema = z.object({
    // Example:
    // NEXT_PUBLIC_API_URL: z.string().url(),
});

function getServerEnv() {
    // Skip validation during build time or when explicitly disabled
    const isBuildTime =
        process.env.SKIP_ENV_VALIDATION === 'true' ||
        process.argv.includes('build') ||
        process.env.CI === 'true' ||
        process.env.NODE_ENV === 'production'; // Add this condition for Docker builds

    if (isBuildTime) {
        // Return mock values during build time to avoid type errors
        // Use process.env values if available, otherwise use mocks
        return {
            NODE_ENV: (process.env.NODE_ENV || 'production') as
                | 'development'
                | 'test'
                | 'production',
            DATABASE_URL:
                process.env.DATABASE_URL ||
                'postgresql://mock:mock@localhost:5432/mock',
            SMTP_HOST: process.env.SMTP_HOST || 'mock',
            SMTP_PORT: parseInt(process.env.SMTP_PORT || '587'),
            SMTP_USER: process.env.SMTP_USER || 'mock',
            SMTP_PASS: process.env.SMTP_PASS || 'mock',
            SMTP_FROM: process.env.SMTP_FROM || 'mock@example.com',
            NEXT_PUBLIC_APP_URL:
                process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000',
            GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID || 'mock',
            GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET || 'mock',
            MICROSOFT_TENANT_ID: process.env.MICROSOFT_TENANT_ID || 'mock',
            MICROSOFT_CLIENT_ID: process.env.MICROSOFT_CLIENT_ID || 'mock',
            MICROSOFT_CLIENT_SECRET:
                process.env.MICROSOFT_CLIENT_SECRET || 'mock',
            NEXT_PUBLIC_PLAUSIBLE_SITE:
                process.env.NEXT_PUBLIC_PLAUSIBLE_SITE || 'mock',
            NEXT_PUBLIC_PLAUSIBLE_URL:
                process.env.NEXT_PUBLIC_PLAUSIBLE_URL || 'mock',
            R2_BUCKET: process.env.R2_BUCKET || 'mock',
            R2_ACCESS_KEY_ID: process.env.R2_ACCESS_KEY_ID || 'mock',
            R2_SECRET_ACCESS_KEY: process.env.R2_SECRET_ACCESS_KEY || 'mock',
            R2_ACCOUNT_ID: process.env.R2_ACCOUNT_ID || 'mock',
            R2_DOMAIN: process.env.R2_DOMAIN || 'mock',
        };
    }

    const parsed = serverSchema.safeParse(process.env);
    if (!parsed.success) {
        console.error(
            '❌ Invalid server environment variables:',
            parsed.error.flatten().fieldErrors,
        );
        throw new Error('Invalid server environment variables');
    }
    return parsed.data;
}

function getClientEnv() {
    if (typeof window === 'undefined') {
        throw new Error('getClientEnv should only be called on the client');
    }
    const parsed = clientSchema.safeParse(process.env);
    if (!parsed.success) {
        console.error(
            '❌ Invalid client environment variables:',
            parsed.error.flatten().fieldErrors,
        );
        throw new Error('Invalid client environment variables');
    }
    return parsed.data;
}

export const env = {
    ...(typeof window === 'undefined' ? getServerEnv() : {}),
    ...(typeof window !== 'undefined' ? getClientEnv() : {}),
};

// Usage:
// import { env } from '@/lib/env';
// env.DATABASE_URL, env.NEXT_PUBLIC_API_URL, etc.
